import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button,
  Toast,
  DatePicker,
  Banner,
  List,
  Box,
} from '@shopify/polaris';
import { SaveBar, useAppBridge } from '@shopify/app-bridge-react';
import useAxiosClient from '../axiosClient';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';

export default function CreateTimeOff() {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const appBridge = useAppBridge();
  const { t } = useTranslation();

  const [timeOffInfo, setTimeOffInfo] = useState({
    selectedStaff: '',
    selectedPolicy: '',
    selectedLocation: '', // NEW: Added location selection
    startDate: null,
    endDate: null,
    reason: '',
  });

  const [staffList, setStaffList] = useState([]);
  const [policyList, setPolicyList] = useState([]);
  const [locationList, setLocationList] = useState([]); // NEW: Added location list
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [errors, setErrors] = useState({});
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const originalData = useRef(timeOffInfo);

  // Initialize selectedDates with the current date
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date(),
  });

  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const handleMonthChange = useCallback(
    (month, year) => setDate({ month, year }),
    []
  );

  // Fetch staff, policy, and location data
  const fetchData = async () => {
    try {
      const [staffResponse, policyResponse, locationResponse] = await Promise.all([
        axiosClient.get('/getStaff'),
        axiosClient.get('/getPolicies'),
        axiosClient.get('/locations'), // NEW: Fetch locations
      ]);

      if (staffResponse.data.success) {
        setStaffList(staffResponse.data.staff);
      } else {
        setToastContent(t('failed_to_load_staff_list'));
        setShowToast(true);
      }

      if (policyResponse.data.success) {
        setPolicyList(policyResponse.data.policies);
      } else {
        setToastContent(t('failed_to_load_policies'));
        setShowToast(true);
      }

      // NEW: Handle location response
      if (locationResponse.data.success) {
        setLocationList(locationResponse.data.locations);
      } else {
        setToastContent(t('failed_to_load_locations'));
        setShowToast(true);
      }
    } catch (error) {
      setToastContent(t('error_loading_data'));
      setShowToast(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update showErrorBanner whenever errors change
  useEffect(() => {
    setShowErrorBanner(Object.keys(errors).length > 0);
  }, [errors]);

  // Function to dismiss the error banner
  const dismissErrorBanner = () => {
    setShowErrorBanner(false);
  };

  // Handle input changes
  const handleChange = (field) => (value) => {
    setTimeOffInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);

    // Clear the error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
  
    // Required fields
    if (!timeOffInfo.selectedStaff) {
      newErrors.selectedStaff = t('please_select_a_staff_member');
    }
  
    if (!timeOffInfo.selectedPolicy) {
      newErrors.selectedPolicy = t('please_select_a_policy');
    }
    
    // NEW: Validate location selection
    if (!timeOffInfo.selectedLocation) {
      newErrors.selectedLocation = t('please_select_a_location');
    }
  
    if (!timeOffInfo.startDate) {
      newErrors.startDate = t('start_date_is_required');
    }
  
    if (!timeOffInfo.endDate) {
      newErrors.endDate = t('end_date_is_required');
    } else if (timeOffInfo.startDate && new Date(timeOffInfo.endDate) < new Date(timeOffInfo.startDate)) {
      newErrors.endDate = t('end_date_cannot_be_before_start_date');
    }
  
    if (!timeOffInfo.reason || timeOffInfo.reason.trim().length < 10) {
      newErrors.reason = t('reason_is_required_and_must_be_at_least_10_characters');
    }
  
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };
  

  // Save time off - MODIFIED TO FIX THE PAYLOAD ISSUE
  const handleSave = async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      setErrors(validation.errors);
      setShowErrorBanner(true);
      setShowToast(true);
      setToastContent(t('please_fill_in_all_required_fields_correctly'));
      return;
    }

    try {
      setIsSaving(true);
      
      // Create a properly formatted payload that matches the backend requirements
      const payload = {
        // Convert selectedStaff (name) to staff_id
        // Find the staff ID from the staffList based on the selected name
        staff_id: staffList.find(staff => 
          `${staff.first_name} ${staff.last_name}` === timeOffInfo.selectedStaff
        )?.id,
        
        // Convert selectedPolicy (name) to policy_id
        // Find the policy ID from the policyList based on the selected name
        policy_id: policyList.find(policy => 
          policy.policy_name === timeOffInfo.selectedPolicy
        )?.id,
        
        // NEW: Add location_id to payload
        location_id: locationList.find(location => 
          location.name === timeOffInfo.selectedLocation
        )?.id,
        
        // Format dates properly for the backend
        start_date: timeOffInfo.startDate ? new Date(timeOffInfo.startDate).toLocaleDateString('en-US') : null,
        end_date: timeOffInfo.endDate ? new Date(timeOffInfo.endDate).toLocaleDateString('en-US') : null,
        
        // Keep reason as is
        reason: timeOffInfo.reason,
      };

      // Check if we have valid IDs before submitting
      if (!payload.staff_id) {
        setErrors(prev => ({ ...prev, selectedStaff: t('could_not_find_staff_id') }));
        setShowToast(true);
        setToastContent(t('staff_id_could_not_be_determined'));
        appBridge.saveBar.hide('create-policy-save-bar');
        setIsSaving(false);
        return;
      }

      if (!payload.policy_id) {
        setErrors(prev => ({ ...prev, selectedPolicy: t('could_not_find_policy_id') }));
        setShowToast(true);
        setToastContent(t('policy_id_could_not_be_determined'));
        appBridge.saveBar.hide('create-policy-save-bar');
        setIsSaving(false);
        return;
      }
      
      // NEW: Check for valid location ID
      if (!payload.location_id) {
        setErrors(prev => ({ ...prev, selectedLocation: t('could_not_find_location_id') }));
        setShowToast(true);
        setToastContent(t('location_id_could_not_be_determined'));
        appBridge.saveBar.hide('create-policy-save-bar');
        setIsSaving(false);
        return;
      }

      const response = await axiosClient.post('/create_timeoff', payload);

      if (response.data.success) {
        originalData.current = timeOffInfo;
        setIsDirty(false);
        shopify.toast.show(t('time_off_created'))
        appBridge.saveBar.hide('create-policy-save-bar');
        navigate('/TimeOff');
      } else {
        shopify.toast.show(response.data.message)
        appBridge.saveBar.hide('create-policy-save-bar');
      }
    } catch (error) {
      // Handle validation errors from the backend
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
        setShowErrorBanner(true);
        setToastContent(error.response.data.message || t('validation_error'));
      } else {
        setToastContent(t('error_creating_time_off'));
        appBridge.saveBar.hide('create-policy-save-bar');
      }
      
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setTimeOffInfo(originalData.current);
    setIsDirty(false);
    setErrors({});
    setShowErrorBanner(false);
    appBridge.saveBar.hide('create-policy-save-bar');
  };
  const handleBackClick = useCallback(() => {
    if (isDirty) {
      appBridge.saveBar.leaveConfirmation()
        .then(() => {
          navigate("/TimeOff");
        })
        .catch(() => {
          // Navigation cancelled
        });
    } else {
      navigate("/TimeOff");
    }
  }, [isDirty, appBridge, navigate]);
  return (
    <Page
      title={t('create_time_off')}
      fullWidth
      backAction={{
        content: t('time_off_management'),
        onAction: handleBackClick ,
      }}
    >
      {showErrorBanner && Object.keys(errors).length > 0 && (
        <Box paddingBlockEnd="400">
          <Banner
            title={t('please_correct_the_following_errors')}
            tone="critical"
            onDismiss={dismissErrorBanner}
          >
            <List type="bullet">
              {Object.entries(errors).map(([field, message]) => (
                <List.Item key={field}>
                  {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${message}`}
                </List.Item>
              ))}
            </List>
          </Banner>
        </Box>
      )}

      <Form onSubmit={handleSave}>
        <Layout>
          {/* Select Staff */}
          <Layout.AnnotatedSection
            title={t('select_staff')}
            description={t('choose_the_staff_member_requesting_time_off')}
          >
            <Card sectioned>
              <Select
                label={t('staff')}
                options={staffList.map((staff) => ({
                  label: `${staff.first_name} ${staff.last_name}`,
                  value: `${staff.first_name} ${staff.last_name}`,
                }))}
                value={timeOffInfo.selectedStaff}
                onChange={handleChange('selectedStaff')}
                placeholder={t('select_staff')}
                error={errors.selectedStaff}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* Select Policy */}
          <Layout.AnnotatedSection
            title={t('select_policy')}
            description={t('choose_the_policy_applicable_for_this_time_off')}
          >
            <Card sectioned>
              <Select
                label={t('policy')}
                options={policyList.map((policy) => ({
                  label: policy.policy_name,
                  value: policy.policy_name,
                }))}
                value={timeOffInfo.selectedPolicy}
                onChange={handleChange('selectedPolicy')}
                placeholder={t('select_policy')}
                error={errors.selectedPolicy}
              />
            </Card>
          </Layout.AnnotatedSection>
          
          {/* NEW: Select Location */}
          <Layout.AnnotatedSection
            title={t('select_location')}
            description={t('choose_the_location_for_this_time_off')}
          >
            <Card sectioned>
              <Select
                label={t('location')}
                options={locationList.map((location) => ({
                  label: location.name,
                  value: location.name,
                }))}
                value={timeOffInfo.selectedLocation}
                onChange={handleChange('selectedLocation')}
                placeholder={t('select_location')}
                error={errors.selectedLocation}
              />
            </Card>
          </Layout.AnnotatedSection>

          {/* Select Dates */}
          <Layout.AnnotatedSection
            title={t('select_dates')}
            description={t('specify_the_start_and_end_dates_for_the_time_off')}
          >
            <Card sectioned>
              <DatePicker
                month={month}
                year={year}
                onChange={(value) => {
                  setSelectedDates(value);
                  setTimeOffInfo((prev) => ({
                    ...prev,
                    startDate: value.start,
                    endDate: value.end,
                  }));
                  setIsDirty(true);

                  // Clear date errors in real-time when dates are selected
                  const errorsToRemove = [];
                  if (value.start && errors.startDate) {
                    errorsToRemove.push('startDate');
                  }
                  if (value.end && errors.endDate) {
                    errorsToRemove.push('endDate');
                  }
                  
                  if (errorsToRemove.length > 0) {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      errorsToRemove.forEach(field => {
                        delete newErrors[field];
                      });
                      return newErrors;
                    });
                  }
                }}
                onMonthChange={handleMonthChange}
                selected={selectedDates}
                allowRange
              />
              {errors.startDate && <p style={{ color: 'red' }}>{errors.startDate}</p>}
              {errors.endDate && <p style={{ color: 'red' }}>{errors.endDate}</p>}
            </Card>
          </Layout.AnnotatedSection>

          {/* Reason */}
          <Layout.AnnotatedSection
            title={t('reason')}
            description={t('provide_a_reason_for_the_time_off_request')}
          >
            <Card sectioned>
              <TextField
                label={t('reason')}
                value={timeOffInfo.reason}
                onChange={handleChange('reason')}
                placeholder={t('enter_reason_for_time_off')}
                error={errors.reason}
                multiline={4}
              />
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </Form>

      {isDirty && (
        <SaveBar
          id="create-policy-save-bar"
          open={isDirty}
        >
          <button
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await handleSave();
              } catch (err) {
                // Error handled silently
              }
            }}
            disabled={isSaving}
          >
            {isSaving ? t('saving') : t('save')}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={isSaving}
          >
            {t('discard')}
          </button>
        </SaveBar>
      )}
    </Page>
  );
}
